import { useState } from "react";
import { useNavigate } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import MenuItemSelect from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import useDateFormat from "../../../hooks/useDateFormat";
import useAcademy from "../../../hooks/useAcademy";
import DeleteAcademyModal from "./modal/DeleteAcademyModal";
import ModuleModal from "./modal/ModuleModal";

const ModuleTable = ({
  modules,
  onTrigger,
  training,
  page,
  setPage,
  limit,
  setLimit,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const navigate = useNavigate();
  const { formatDate } = useDateFormat();
  const { toggleAcademy } = useAcademy();
  const [loading, setLoading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openModule, setOpenModule] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleAction = async (action, id) => {
    const resource = "trainingModule";
    const toggleName = "toggleActive";

    switch (action) {
      case "viewAll":
        setAnchorEl(null);
        setSelectedRow(null);
        handleRowClick(selectedRow);
        break;
      case "edit":
        setAnchorEl(null);
        setOpenModule(true);
        break;
      case "deactivate":
        await toggleAcademy(setLoading, id, resource, toggleName);
        setAnchorEl(null);
        setSelectedRow(null);
        void onTrigger();
        break;
      case "delete":
        setAnchorEl(null);
        setOpenDeleteModal(true);
        break;
      default:
        console.warn("Unknown action:", action);
    }
  };

  const handleRowClick = (module) => {
    navigate(
      `/settings/academy/module/videos?trainingModuleId=${module?._id}`,
      {
        state: { module },
      }
    );
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPage(1);
  };

  const totalModules = modules?.meta?.total || 0;
  const totalPages = Math.ceil(totalModules / limit);

  return (
    <>
      <div className="">
        <div className="overflow-x-auto rounded-lg shadow">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-blue-100 text-left text-sm font-medium text-gray-600">
                <th className="px-6 py-3">Module Name</th>
                <th className="px-6 py-3">Description</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Number of videos</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="text-sm text-gray-700">
              {modules?.data?.map((row, index) => (
                <tr
                  key={index}
                  className="border-b hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleRowClick(row)}
                >
                  <td className="px-6 py-4">{row.title}</td>
                  <td className="px-6 py-4">
                    {row.description || "no description"}
                  </td>
                  <td className="px-6 py-4">{formatDate(row.createdAt)}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-3 py-1 rounded-md text-xs font-semibold ${
                        row.isActive
                          ? "bg-green-100 text-green-600"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {row?.videoCount}
                    </span>
                  </td>
                  <td className="px-6 py-4 flex items-center gap-2">
                    <span
                      className={`h-3 w-3 rounded-full ${
                        row.isActive ? "bg-green-500" : "bg-red-500"
                      }`}
                    ></span>
                    {row.isActive ? "Active" : "InActive"}
                  </td>
                  <td
                    className="px-6 py-4 text-gray-500"
                    onClick={(e) => handleClick(e, row)}
                  >
                    <MoreVertIcon className="h-5 w-5 cursor-pointer" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col sm:flex-row justify-end items-center mt-4 gap-4">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
            color="primary"
          />
          <FormControl size="small" className="w-28">
            <InputLabel id="limit-select-label">Rows per page</InputLabel>
            <Select
              labelId="limit-select-label"
              value={limit}
              label="Rows per page"
              onChange={handleLimitChange}
            >
              <MenuItemSelect value={10}>10</MenuItemSelect>
              <MenuItemSelect value={20}>20</MenuItemSelect>
              <MenuItemSelect value={30}>30</MenuItemSelect>
            </Select>
          </FormControl>
        </div>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={() => handleAction("viewAll", selectedRow?._id)}>
            View All
          </MenuItem>
          <MenuItem onClick={() => handleAction("edit", selectedRow?._id)}>
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => handleAction("deactivate", selectedRow?._id)}
          >
            {selectedRow?.isActive ? "Deactivate" : "Activate"}
          </MenuItem>
          <MenuItem onClick={() => handleAction("delete", selectedRow?._id)}>
            Delete
          </MenuItem>
        </Menu>
      </div>

      {openDeleteModal && (
        <DeleteAcademyModal
          onTrigger={onTrigger}
          modalName={"Module"}
          name={selectedRow?.title}
          accademyCategory={4}
          createdBy={training?.trainersName}
          setOpenDeleteModal={setOpenDeleteModal}
          id={selectedRow?._id}
          subCategory={selectedRow?.videoCount}
        />
      )}

      {openModule && (
        <ModuleModal
          trainingID={training?._id}
          setOpenModule={setOpenModule}
          onTrigger={onTrigger}
          module={selectedRow}
        />
      )}
    </>
  );
};

export default ModuleTable;
